import React, { useState, useRef } from "react";
import { ChevronDown } from "lucide-react";

const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

function SubItem({ item, isActive, onClick, popout }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${testId(item.label)}`}
      className={[
        "flex items-center w-full rounded-lg h-8 text-[13px] px-3 transition-colors duration-150 select-none",
        isActive ? "bg-white font-semibold shadow-sm" : popout ? "text-white/85 hover:bg-white/10" : "text-white/70 hover:bg-white/10 hover:text-white",
      ].join(" ")}
      style={isActive ? { color: "#001C60" } : {}}
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="ml-2.5 truncate">{item.label}</span>
    </button>
  );
}

export function NavGroup({ group, collapsed, open, onToggle, activePath, onSelect }) {
  const Icon = group.icon;
  const hasActive = group.children.some((c) => c.path === activePath);
  const btnRef = useRef(null);
  const [pop, setPop] = useState(null);
  const showPop = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPop({ top: r.top, left: r.right + 6 });
  };

  if (collapsed) {
    return (
      <div className="px-1 mb-0.5" onMouseEnter={showPop} onMouseLeave={() => setPop(null)}>
        <button
          ref={btnRef}
          data-testid={`nav-group-${testId(group.label)}`}
          className={[
            "flex items-center justify-center w-full rounded-lg h-9 transition-colors duration-150",
            hasActive ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white",
          ].join(" ")}
        >
          <Icon size={16} />
        </button>
        {pop && (
          <div
            data-testid={`nav-group-popout-${testId(group.label)}`}
            className="fixed z-[70] w-52"
            style={{ top: pop.top, left: pop.left - 6, paddingLeft: 6 }}
          >
            <div className="rounded-xl p-1.5 shadow-2xl" style={{ backgroundColor: "#001C60" }}>
              <div className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-white/50">{group.label}</div>
              {group.children.map((c) => (
                <SubItem key={c.path} item={c} popout isActive={c.path === activePath} onClick={() => { setPop(null); onSelect(c.path); }} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-2 mb-0.5">
      <button
        onClick={onToggle}
        data-testid={`nav-group-${testId(group.label)}`}
        aria-expanded={open}
        className={[
          "flex items-center w-full rounded-lg h-9 text-[13px] px-3 transition-colors duration-150 select-none",
          hasActive && !open ? "bg-white/15 text-white font-semibold" : "text-white/80 hover:bg-white/10 hover:text-white",
        ].join(" ")}
      >
        <Icon size={16} className="flex-shrink-0" />
        <span className="ml-2.5 truncate">{group.label}</span>
        <ChevronDown size={14} className={`ml-auto flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className="grid transition-[grid-template-rows,opacity] duration-200 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
      >
        <div className="overflow-hidden">
          <div className="ml-3 pl-2 mt-0.5 border-l border-white/15 space-y-0.5" data-testid={`nav-group-items-${testId(group.label)}`}>
            {group.children.map((c) => (
              <SubItem key={c.path} item={c} isActive={c.path === activePath} onClick={() => onSelect(c.path)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
